import ResourceManager from "../../components/ResourceManager"
import { StringMap } from "i18next"
import { useOptions } from "../../options/hooks/use-options"
import { useProjectMutations } from "../hooks/use-project-mutations"
import { useI18n } from "../../hooks/use-i18n"
import { ProjectViewModel } from "../api/project"

const ProjectParticipantsWidget = ({ project }: { project: ProjectViewModel }) => {
  const translations = useTranslations(defaultTranslations)
  const { membershipOptions } = useOptions(project.maintainerId)
  const { addParticipants, removeParticipants } = useProjectMutations()
  const isNotManager = ({ id }: { id: string }) => project.managers.findIndex((m) => m.id === id) < 0

  const handleAddParticipants = (ids: string[]) => {
    const participants = membershipOptions
      .filter((member) => ids.includes(member.id))
      .map((member) => ({ name: member.name, id: member.id }))
    addParticipants(project.id, participants)
  }

  const handleRemoveParticipants = (id: string) => {
    const participants = project.participants
      .filter((member) => member.id === id)
      .map((member) => ({ name: member.name, id: member.id }))
    removeParticipants(project.id, participants)
  }

  const sortedMembershipOptions = membershipOptions
    .filter(isNotManager)
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedParticipants = project.participants
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedMembershipOptions}
      resources={sortedParticipants}
      onAddResources={handleAddParticipants}
      onRemoveResource={handleRemoveParticipants}
      canAddResource={project.canUpdateParticipants}
      canRemoveResource={project.canUpdateParticipants}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t } = useI18n("project")
  const translations = (t?.participantsWidget || {}) as StringMap

  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Participants",
  emptyOptionsMessage: "No members in this organisation",
  emptyResourcesMessage: "No participant in this project",
}

type Translations = typeof defaultTranslations

export default ProjectParticipantsWidget
